import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, FileText, Factory, ShieldCheck, GitCompareArrows, Database } from "lucide-react";
import { StatusLegend } from "@/components/StatusChip";

const HERO_IMG =
  "https://images.pexels.com/photos/32399139/pexels-photo-32399139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1800";
const FACILITY_IMG =
  "https://images.unsplash.com/photo-1771531072574-af6ed6b954c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZmFjaWxpdHklMjBjbGVhbnxlbnwwfHx8fDE3ODcwMzQ2Mzh8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { n: "01", t: "Search the part you have", d: "Enter a manufacturer, brand or part number. ADIT Connect finds the item in the normalized specification database.", Icon: Search },
  { n: "02", t: "Read the normalized spec", d: "Thread, armour, cable range, material, Ex protection, gas group, IP rating, temperature and certificate reference — one consistent format.", Icon: Database },
  { n: "03", t: "Compare equivalents", d: "See technically comparable parts from other manufacturers, parameter by parameter, with a clear conformity status on each line.", Icon: GitCompareArrows },
  { n: "04", t: "Submit an RFQ", d: "The product and its specification prefill into the request. Add quantity, delivery date, location and attachments.", Icon: FileText },
];

const BRANDS = ["CMP Products", "Hawke International", "Peppers Cable Glands", "Bimed Teknik", "AFTEC"];

export default function Home() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const go = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : "/search");
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain overflow-hidden bg-[#102A43]">
        <img
          src={HERO_IMG}
          alt="Industrial refinery at dusk"
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#102A43]/95 via-[#102A43]/82 to-[#163B65]/62" />
        <div className="relative mx-auto max-w-[1240px] px-5 py-16 sm:py-24 lg:py-28">
          <p className="reveal text-[10px] font-semibold uppercase tracking-[0.22em] text-[#D9A441]">
            Phase 1 — Spec Intelligence
          </p>
          <h1
            className="reveal mt-5 max-w-3xl font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "80ms" }}
          >
            Industrial Spec Intelligence &amp; Sourcing for the GCC
          </h1>
          <p
            className="reveal mt-6 max-w-2xl text-base leading-relaxed text-[#bcccdc] md:text-lg"
            style={{ animationDelay: "160ms" }}
          >
            Find a part, understand its real technical specification, and see which products from
            other manufacturers genuinely meet the same requirement — before you buy.
          </p>

          <form
            onSubmit={go}
            className="reveal mt-9 flex max-w-2xl flex-col gap-2 rounded-[14px] border border-white/15 bg-white/[0.07] p-2 backdrop-blur-md sm:flex-row"
            style={{ animationDelay: "240ms" }}
          >
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Manufacturer, brand or part number — e.g. 20 A2F, Hawke 501/453"
              className="w-full rounded-[9px] bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#829ab1] focus:ring-2 focus:ring-[#147D78]"
            />
            <button
              data-testid="hero-search-btn"
              type="submit"
              className="flex items-center justify-center gap-2 rounded-[9px] bg-[#D9A441] px-5 py-3 text-sm font-bold text-[#17212b] transition-transform active:scale-[0.97]"
            >
              Search Products &amp; Equivalents <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="reveal mt-6 flex flex-wrap gap-3" style={{ animationDelay: "320ms" }}>
            <Link
              to="/rfq"
              data-testid="hero-rfq-btn"
              className="rounded-[9px] bg-white px-5 py-3 text-sm font-bold text-[#102A43] transition-transform active:scale-[0.97]"
            >
              Submit an RFQ
            </Link>
            <Link
              to="/register?role=buyer"
              data-testid="hero-buyer-register-btn"
              className="rounded-[9px] border border-white/25 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              Create a buyer account
            </Link>
            <Link
              to="/join-supplier"
              data-testid="hero-supplier-btn"
              className="rounded-[9px] border border-white/25 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              Join as a Supplier
            </Link>
          </div>

          <div className="reveal mt-12 border-t border-white/10 pt-6" style={{ animationDelay: "400ms" }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#829ab1]">
              Manufacturers currently mapped in the Phase 1 reference catalogue
            </p>
            <div className="mt-3 flex flex-wrap gap-x-7 gap-y-2">
              {BRANDS.map((b) => (
                <span key={b} className="font-display text-sm font-bold tracking-tight text-[#bcccdc]">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-[1240px] px-5 py-16 lg:py-24" data-testid="how-it-works">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
          How ADIT Connect works
        </p>
        <h2 className="mt-3 max-w-2xl font-display text-2xl font-black tracking-tight text-[#102A43] sm:text-3xl">
          Four steps, from a part number to a priced enquiry
        </h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`how-step-${i + 1}`}
              className="reveal rounded-[14px] border border-[#DFE6ED] bg-white p-5 transition-shadow hover:shadow-sm"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="flex items-center justify-between">
                <span className="grid h-10 w-10 place-items-center rounded-[9px] bg-[#eef2f7] text-[#102A43]">
                  <s.Icon className="h-5 w-5" />
                </span>
                <span className="font-display text-xl font-black text-[#DFE6ED]">{s.n}</span>
              </div>
              <h3 className="mt-4 font-display text-base font-bold tracking-tight text-[#102A43]">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#667788]">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CONFORMITY */}
      <section className="border-y border-[#DFE6ED] bg-white">
        <div className="mx-auto grid max-w-[1240px] items-center gap-12 px-5 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
              No compliance percentages
            </p>
            <h2 className="mt-3 font-display text-2xl font-black tracking-tight text-[#102A43] sm:text-3xl">
              Every parameter gets a decision, not a score
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#667788] md:text-base">
              A gland that is 90% compliant is still the wrong gland. ADIT Connect states the
              outcome for each individual technical parameter so an engineer can sign off — or
              reject — with evidence.
            </p>
            <div className="mt-6">
              <StatusLegend />
            </div>
            <ul className="mt-8 space-y-3 text-sm text-[#334e68]">
              {[
                "Specifications normalized to one parameter set across all manufacturers",
                "Certificate reference carried on every mapped part number",
                "Cross-references maintained by ADIT, not crowd-sourced",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#147D78]" />
                  {t}
                </li>
              ))}
            </ul>
            <Link
              to="/search"
              data-testid="conformity-search-link"
              className="mt-8 inline-flex items-center gap-2 rounded-[9px] bg-[#102A43] px-5 py-3 text-sm font-bold text-white transition-transform active:scale-[0.97]"
            >
              Open product search <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="overflow-hidden rounded-[14px] border border-[#DFE6ED]">
            <img src={FACILITY_IMG} alt="Industrial facility" className="h-[340px] w-full object-cover lg:h-[420px]" />
          </div>
        </div>
      </section>

      {/* SUPPLIER STRIP */}
      <section className="mx-auto max-w-[1240px] px-5 py-16 lg:py-20">
        <div className="grid gap-8 rounded-[14px] border border-[#DFE6ED] bg-[#102A43] p-8 lg:grid-cols-[1fr_auto] lg:items-center lg:p-12">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#D9A441]">
              <Factory className="h-3.5 w-3.5" /> For manufacturers &amp; suppliers
            </span>
            <h2 className="mt-4 font-display text-2xl font-black tracking-tight text-white sm:text-3xl">
              Get your range mapped into GCC specifications
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#bcccdc] md:text-base">
              Register your interest and our engineering team will normalize your catalogue into the
              ADIT parameter set so buyers can find your parts as verified equivalents.
            </p>
          </div>
          <Link
            to="/join-supplier"
            data-testid="supplier-cta-btn"
            className="inline-flex items-center justify-center gap-2 rounded-[9px] bg-[#D9A441] px-6 py-3.5 text-sm font-bold text-[#17212b] transition-transform active:scale-[0.97]"
          >
            Join as a Supplier <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
